import { useMemo, useRef, useState } from "react";
import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";

import type { Property } from "@shared/schema";

import MapView from '@/features/map/MapView';
import PropertyList from "../list/PropertyList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Filter } from "lucide-react";
import { useLocation } from "wouter";
import "./BottomSheet.css";

type MobileSearchScreenProps = {
  properties: Property[];
  selectedPropertyId?: string | null;
  onBoundsChanged: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onSaveSearch?: () => void;
  onPropertySelect?: (property: Property) => void;
  onScheduleTour?: (property: Property) => void;
  onContactAgent?: (property: Property) => void;
  filters: {
    city: string;
    minPrice: string;
    maxPrice: string;
    bedrooms: string;
    bathrooms: string;
    propertyType: string;
  };
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  isModalOpen?: boolean;
};

const MOBILE_LIST_LIMIT = 150;

export default function MobileSearchScreen({
  properties,
  selectedPropertyId,
  onBoundsChanged,
  onSaveSearch,
  onPropertySelect,
  onScheduleTour,
  onContactAgent,
  filters,
  onFilterChange,
  onClearFilters,
  isModalOpen = false,
}: MobileSearchScreenProps) {
  const [, setLocation] = useLocation();
  const sheetRef = useRef<{ snapTo: (height: number, opts?: { source?: string }) => void; height: number } | null>(null);
  const [pendingSnap, setPendingSnap] = useState<number | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [peeked, setPeeked] = useState(true);

  const snapPoints = useMemo(() => [0.18, 0.58, 0.96], []);
  const limitedProperties = useMemo(
    () => properties.slice(0, MOBILE_LIST_LIMIT),
    [properties],
  );
  const isTruncated = properties.length > MOBILE_LIST_LIMIT;
  const activeFilterCount = useMemo(
    () =>
      Object.entries(filters).filter(([key, value]) => {
        if (key === "city") return false;
        return Boolean(value);
      }).length,
    [filters],
  );

  const handleSnapTo = (ratio: number) => {
    const sheet = sheetRef.current;
    if (!sheet) {
      setPendingSnap(ratio);
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    sheet.snapTo(ratio * viewportHeight, { source: "controls" });
  };

  return (
    <>
      <div className="mobile-root" data-modal-open={isModalOpen ? "true" : "false"}>
        <div className="mobile-map-wrap">
          <MapView
            properties={properties}
            selectedPropertyId={selectedPropertyId ?? undefined}
            onPropertySelect={onPropertySelect}
            onBoundsChange={onBoundsChanged}
            className="h-full"
            height="100%"
            modalOpen={isModalOpen}
          />

          <div className="mobile-topbar">
            <Button
              variant="ghost"
              size="sm"
              className="map-back-btn"
              onClick={() => setLocation("/")}
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="map-filter-btn"
              onClick={() => {
                handleSnapTo(0.96);
                setFilterPanelOpen(true);
              }}
            >
              <Filter className="w-4 h-4 mr-1" /> Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-ocean-blue text-[10px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          <div className="mobile-fabs">
            <button className="pill" onClick={() => handleSnapTo(0.18)}>
              Map
            </button>
            <button className="pill" onClick={() => handleSnapTo(0.6)}>
              List
            </button>
            <button
              className="cta"
              onClick={() => {
                handleSnapTo(0.96);
                onSaveSearch?.();
              }}
            >
              Save Search
            </button>
          </div>
        </div>

        <BottomSheet
          ref={(node) => {
            if (!node) return;
            sheetRef.current = node;
            if (pendingSnap != null) {
              const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
              node.snapTo(pendingSnap * viewportHeight, { source: "init" });
              setPendingSnap(null);
            }
          }}
          open
          blocking={isModalOpen}
          expandOnContentDrag
          defaultSnap={({ maxHeight }) => 0.6 * maxHeight}
          snapPoints={({ maxHeight }) => snapPoints.map((ratio) => ratio * maxHeight)}
          skipInitialTransition
          style={{ pointerEvents: isModalOpen ? 'none' : 'auto' }}
          onSpringEnd={({ source, height }) => {
            if (source === "dragging" || source === "controls") {
              const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
              setPeeked(height <= viewportHeight * 0.25);
            }
          }}
          header={
            <div className="sheet-grabber">
              <div className="grabber" />
              <div className="results">
                {properties.length.toLocaleString()} results
              </div>
            </div>
          }
        >
          <div className="space-y-3">
            {filterPanelOpen && (
              <div className="space-y-3 rounded-2xl border border-slate-200 bg-white/95 p-4 text-sm text-slate-900 shadow-lg">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Filters</span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-800"
                      onClick={() => {
                        onClearFilters();
                        setFilterPanelOpen(false);
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      className="bg-ocean-blue text-white hover:bg-bright-blue"
                      onClick={() => setFilterPanelOpen(false)}
                    >
                      Done
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label className="text-xs uppercase text-slate-500">Min Price</Label>
                    <Input
                      value={filters.minPrice}
                      onChange={(e) => onFilterChange("minPrice", e.target.value)}
                      placeholder="$500,000"
                      inputMode="numeric"
                      className="mt-1 bg-white border-slate-200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-slate-500">Max Price</Label>
                    <Input
                      value={filters.maxPrice}
                      onChange={(e) => onFilterChange("maxPrice", e.target.value)}
                      placeholder="$10,000,000"
                      inputMode="numeric"
                      className="mt-1 bg-white border-slate-200"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase text-slate-500">Bedrooms</Label>
                      <select
                        value={filters.bedrooms}
                        onChange={(e) => onFilterChange("bedrooms", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Any</option>
                        <option value="1">1+</option>
                        <option value="2">2+</option>
                        <option value="3">3+</option>
                        <option value="4">4+</option>
                        <option value="5">5+</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase text-slate-500">Bathrooms</Label>
                      <select
                        value={filters.bathrooms}
                        onChange={(e) => onFilterChange("bathrooms", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Any</option>
                        <option value="1">1+</option>
                        <option value="2">2+</option>
                        <option value="3">3+</option>
                        <option value="4">4+</option>
                        <option value="5">5+</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-slate-500">Property Type</Label>
                    <select
                      value={filters.propertyType}
                      onChange={(e) => onFilterChange("propertyType", e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                    >
                      <option value="">All Types</option>
                      <option value="Single Family">House</option>
                      <option value="Condo">Condo</option>
                      <option value="Estate">Estate</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {isTruncated && (
              <p className="text-xs text-slate-300">
                Showing first {MOBILE_LIST_LIMIT.toLocaleString()} listings. Refine filters or pan the map for more.
              </p>
            )}
            {!peeked && (
              <div className="text-xs text-slate-400 text-center">Drag down to peek map</div>
            )}
            <PropertyList
              properties={limitedProperties}
              selectedId={selectedPropertyId ?? undefined}
              onSelect={onPropertySelect}
              onScheduleTour={onScheduleTour}
              onContactAgent={onContactAgent}
              cardIdPrefix="card-"
              cardImageProps={{ loading: "lazy" }}
            />
          </div>
        </BottomSheet>
      </div>
    </>
  );
}

